import React, { memo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { ContextMenu } from 'primereact/contextmenu';
import { Dialog } from 'primereact/dialog';
import { Tree } from 'primereact/tree';
import { TreeNode } from 'primereact/treenode';
import { STATUS_CODE } from 'components/HHQComponents/common/constants';
import UploadComponent from './Library-upload/index';
import PaginatorCustom from 'components/HHQComponents/PaginatorCustom';
import moment from 'moment';
import './index.css';
import './Library-upload/index.css';

interface LibraryProps {
  chooseImage?: (item: any) => void;
  callFromAdmin?: number;
  baseImageUrl: string;
  toast?: React.RefObject<any>;
  apiService: {
    getLibrariesFolder: Function;
    getLibrariesFile: Function;
    deleteLibrary: Function;
    deleteLibraryFile: Function;
    setFile: (file: File, folder: string) => Promise<any>;
    setLibrary: Function;
  };
  setShowLoading?: (show: boolean) => void;
}

interface FileItem {
  id: string;
  name: string;
  url: string;
  type: string;
}

const LibraryComponent: React.FC<LibraryProps> = ({ chooseImage, callFromAdmin, baseImageUrl, toast, apiService, setShowLoading }) => {
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [selectedTreeNode, setSelectedTreeNode] = useState<any>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadType, setUploadType] = useState('file');
  const [fileList, setFileList] = useState<FileItem[]>([]);
  const contextMenu = useRef<any>(null);
  const [pagination, setPagination] = useState({ first: 0, rows: 15, current: 1, total: 0 });
  const [isDragging, setIsDragging] = useState(false);


  const contextMenuItems = [
    { label: 'Thêm thư mục', icon: 'pi pi-folder', command: () => handleOpenUpload('folder') },
    { label: 'Xóa thư mục', icon: 'pi pi-trash', command: () => handleDeleteFolder() },
    { label: 'Thêm file', icon: 'pi pi-file', command: () => handleOpenUpload('file') },
  ];

  useEffect(() => { loadFolders(); }, []);
  useEffect(() => { loadFiles(); }, [pagination.first, pagination.rows, pagination.current]);

  const loadFolders = () => {
    const selectedId = selectedTreeNode?.nodeId;
    setShowLoading?.(true);
    apiService.getLibrariesFolder({ callFromAdmin: callFromAdmin ?? 1, folderId: '', type: '' })
      .then((res: any) => {
        const rawData = res.data.data.data;
        const map: { [key: string]: any } = {};
        rawData.forEach((item: any) => {
            map[item.id] = {
                key: item.id,
                nodeId: item.id,
                label: item.name,
                data: item,
                children: [],
                expandedIcon: 'pi pi-folder-open',
                collapsedIcon: 'pi pi-folder',
            };
        });
        rawData.forEach((item: any) => {
          if (item.parentId && map[item.parentId]) {
            map[item.parentId].children.push(map[item.id]);
            if (item.id === selectedId) {
              map[item.parentId].expanded = true;
            }
          }
        });
        const root = rawData.filter((item: any) => !item.parentId).map((item: any) => map[item.id]);
        setTreeData(root);
      })
      .finally(() => setShowLoading?.(false));
  };

  const loadFiles = async (node?: any) => {
    const targetNode = node ?? selectedTreeNode;
    if (!targetNode) return;
    setShowLoading?.(true);
    try {
      const res = await apiService.getLibrariesFile({
        folderId: targetNode.nodeId,
        callFromAdmin: callFromAdmin ?? 1,
        type: '',
        offSet: pagination.first,
        pageSize: pagination.rows
      });
      setFileList(res.data.data.data);
      setPagination(p => ({ ...p, total: res.data.data.recordsFiltered }));
    } catch (_) {}
    setShowLoading?.(false);
  };

  const handleOpenUpload = (type = 'file') => {
    setUploadType(type);
    setShowUpload(true);
  };

  const handleDeleteFolder = async () => {
    const res = await apiService.getLibrariesFile({ folderId: selectedTreeNode.nodeId, callFromAdmin: callFromAdmin ?? 1, type: '', offSet: 0, pageSize: 1000 });
    if (res.data.data.data.length) {
      toast?.current?.show({ severity: 'error', summary: 'Thông báo', detail: 'Vui lòng xóa hết File, Thư mục con trong thư mục này!', life: 3000 });
    } else {
      confirmDialog({
        message: 'Bạn có chắc chắn muốn xóa thư mục không?',
        header: 'Xác nhận',
        acceptLabel: 'Đồng ý',
        rejectLabel: 'Từ chối',
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
          try {
            const delRes = await apiService.deleteLibrary(selectedTreeNode.nodeId);
            if (delRes.data.statusCode === STATUS_CODE.SUCCESS) {
              toast?.current?.show({ severity: 'success', summary: 'Thông báo', detail: 'Xóa thành công!', life: 3000 });
              loadFolders();
            } else {
              toast?.current?.show({ severity: 'error', summary: 'Thông báo', detail: delRes.data.error, life: 3000 });
            }
          } catch {
            toast?.current?.show({ severity: 'error', summary: 'Thông báo', detail: 'Xóa thư mục thất bại!', life: 3000 });
          }
        }
      });
    }
  };

  const handleDeleteFile = (file: FileItem) => {
    confirmDialog({
      message: 'Bạn có chắc chắn muốn xóa ảnh không?',
      header: 'Xác nhận',
      acceptLabel: 'Đồng ý',
      rejectLabel: 'Từ chối',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        setShowLoading?.(true);
        try {
          const res = await apiService.deleteLibraryFile(file.id, file.url);
          if (res?.data?.data?.valid) {
            toast?.current?.show({ severity: 'success', summary: 'Thông báo', detail: 'Xóa thành công!', life: 3000 });
            setFileList(prev => prev.filter(f => f.id !== file.id));
          } else {
            toast?.current?.show({ severity: 'error', summary: 'Thông báo', detail: 'Xóa ảnh thất bại', life: 3000 });
          }
        } catch {
          toast?.current?.show({ severity: 'error', summary: 'Thông báo', detail: 'Xóa thất bại!', life: 3000 });
        } finally {
          setShowLoading?.(false);
        }
      }
    });
  };

  const handleFileDropOrPaste = async (file: File) => {
    if (!file || !file.type.startsWith('image')) return;
    setShowLoading?.(true);

    const reader = new FileReader();
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = async () => {
      reader.readAsDataURL(file);
      reader.onloadend = async () => {
        const fileInfo = {
          url: '',
          name: file.name,
          size: file.size.toString(),
          type: file.type.startsWith('image') ? 'image' :
                file.type.startsWith('video') ? 'video' :
                file.type.startsWith('audio') ? 'audio' : 'link',
          width: img.width,
          height: img.height,
          parentId: selectedTreeNode?.nodeId ?? '',
          createDate: moment().format('YYYY-MM-DD HH:mm:ss'),
          modifiedDate: moment().format('YYYY-MM-DD HH:mm:ss'),
          fromAdmin: callFromAdmin ?? 1
        };

        try {
          const res = await apiService.setFile(file, '');
          fileInfo.url = res.data.data;
          await apiService.setLibrary(fileInfo);
          loadFiles();
        } catch (error) {
          toast?.current?.show({ severity: 'error', summary: 'Thông báo', detail: 'Tải ảnh thất bại', life: 3000 });
        } finally {
          setShowLoading?.(false);
        }
      };
    };
  };


  useEffect(() => {
    const container = document.querySelector('.image-list');
    if (!container) return;

    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (items) {
        Array.from(items).forEach((item) => {
          if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file) {
              handleFileDropOrPaste(file);
            }
          }
        });
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleFileDropOrPaste(files[0]);
      }
    };

    container.addEventListener('paste', handlePaste as any);
    container.addEventListener('dragover', handleDragOver as any);
    container.addEventListener('dragleave', handleDragLeave as any);
    container.addEventListener('drop', handleDrop as any);

    return () => {
      container.removeEventListener('paste', handlePaste as any);
      container.removeEventListener('dragover', handleDragOver as any);
      container.removeEventListener('dragleave', handleDragLeave as any);
      container.removeEventListener('drop', handleDrop as any);
    };
  }, [selectedTreeNode]);


  return (
    <div className="grid quan-li-thu-vien">
            {showUpload && createPortal(
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99999999999998
          }} onClick={() => setShowUpload(false)} />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '8px',
            padding: '20px',
            zIndex: 99999999999999,
            width: '600px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>Upload</h3>
              <button onClick={() => setShowUpload(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <UploadComponent
              uploadType={uploadType as 'file' | 'folder' | 'root'}
              setFile={apiService.setFile}
              setShowLoading={setShowLoading}
              toast={toast}
              folderUpload="image"
              updateMediaSuccess={(file: any) => {
                  setShowUpload(false);
                  setShowLoading?.(true);
                  file.parentId = uploadType === 'root' ? '' : selectedTreeNode?.nodeId;
                  file.fromAdmin = callFromAdmin ?? 0;
                  apiService.setLibrary(file)
                      .then((res: any) => {
                      if (res.data.statusCode === STATUS_CODE.SUCCESS) {
                          toast?.current?.show({ severity: 'success', summary: 'Thông báo', detail: 'Thêm thành công', life: 3000 });
                          if(uploadType == 'file') {
                            loadFiles();
                          }
                          else {
                            loadFolders();
                          }
                      } else {
                          toast?.current?.show({ severity: 'error', summary: 'Thông báo', detail: res.data.error, life: 3000 });
                      }
                      })
                      .catch(() => {
                      toast?.current?.show({ severity: 'error', summary: 'Thông báo', detail: 'Thêm file thất bại', life: 3000 });
                      })
                      .finally(() => {
                      setShowLoading?.(false);
                      });
              }}
            />
            </div>
          </>,
          document.body
        )}
      <div className="col-12 md:col-3 menu-folder">
        <Button label="Thư mục gốc" icon="pi pi-folder" className="p-button-outlined p-button-secondary mb-2 p-button-sm" onClick={() => handleOpenUpload('root')} />
        <ContextMenu model={contextMenuItems} ref={contextMenu} onHide={() => setSelectedNode(null)} />
        <Tree
          value={treeData}
          onContextMenu={e => (contextMenu.current?.show(e.originalEvent), setSelectedNode(e.node))}
          onSelectionChange={e => setSelectedNode(e.value)}
          selectionMode="single"
          onSelect={e => { 
            setSelectedTreeNode(e.node); 
            loadFiles(e.node); 
          }}
          onContextMenuSelectionChange={e => setSelectedNode(e.value)}
          selectionKeys={selectedNode}
        />
      </div>
      <div className="col-12 md:col-9">
        <div className="image_actions text-right">
            <Button
                type="button"
                label="Thêm file"
                icon="pi pi-file"
                className="p-button-outlined p-button-secondary mr-2 mb-2 p-button-sm"
                onClick={() => handleOpenUpload('file')}
            />
            <Button
                type="button"
                label="Thêm thư mục"
                icon="pi pi-folder"
                className="p-button-outlined p-button-secondary mb-2 p-button-sm"
                onClick={() => handleOpenUpload('folder')}
            />
        </div>
        <div className="image-list" onDragOver={(e) => e.preventDefault()} style={{ minHeight: 50 }}>
          {isDragging && (
            <div className="drop-overlay">
              <div className="drop-message">📂 Thả ảnh vào đây để tải lên</div>
            </div>
          )}
          {fileList.map(file => (
            <div className="image-item" key={file.id}>
              {file.type === 'image' ? <img src={baseImageUrl + file.url} alt={file.name} /> : null}
              <div className="image-item--actions">
                <div className="image-item--actions-left">
                  <i className={`pi ${file.type === 'image' ? 'pi-images' : 'pi-file'}`} />
                </div>
                <div className="image-item--action-right">
                  <Button icon="pi pi-check" className="p-button-rounded p-button-text p-button-sm" onClick={() => chooseImage ? chooseImage(file) : null } />
                  <Button icon="pi pi-times" className="p-button-rounded p-button-danger p-button-text p-button-sm" onClick={() => handleDeleteFile(file)} />
                </div>
              </div>
              <div className="image-item--info">
                <div className="image-item--info-name">{file.name}</div>
              </div>
            </div>
          ))}
        </div>
        {fileList.length > 0 && (
          <PaginatorCustom
            first={pagination.first}
            rows={pagination.rows}
            totalRecords={pagination.total}
            onPageChange={e => setPagination({ ...pagination, first: e.first, rows: e.rows })}
          />
        )}
      </div>
    </div>
  );
};

export default memo(LibraryComponent);
